import { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendar } from "@fortawesome/free-solid-svg-icons"
import { ChevronLeft, ChevronRight, CloudCog } from "lucide-react"
import { useNavigate } from "react-router-dom"
import useLocalStorageState from "use-local-storage-state"
import { IconChecklist, IconPoint } from "@tabler/icons-react"
import { GetRequest } from "../../../../Utils/hooks/ApiHandler"
import { MeetingDetails } from "../MeetingDetails"
import { Col, Row } from "react-bootstrap"
import nodata from "../../../../Utils/assets/img/leads_no_data_found_removebg.png";
import { MeetingCard } from "../../../../CommonComponents/MeetingCard"

export const TaskBoardMeeting = () => {
    const [customer, setCustomer] = useLocalStorageState("customer")
    const [meetings, setMeetings] = useState(null)
    const [historyMeetings, setHistoryMeetings] = useState(null)
    const [isLoadingMeetings, setIsLoadingMeetings] = useState(false)
    const navigate = useNavigate()
    const headers = {
        Authorization: customer?.token?.authorization,
        Authentication: customer?.token?.authentication,
    }
    const extras = {
        customer,
        setCustomer,
        rf: customer?.token?.authentication,
        navigate,
    }

    // state for storing the respective meeting data to show in meeting modal
    const [currentMeetingData, setCurrentMeetingData] = useState(null)
    const [currMeetingStatus, setMeetingStatus] = useState()

    const [currentDate, setCurrentDate] = useState(new Date())
    const [visibleStartDate, setVisibleStartDate] = useState(new Date())
    const [selectedDay, setSelectedDay] = useState(new Date().getDate())
    const [selectedDateForApi, setSelectedDateForApi] = useState(new Date())

    const [showMeetingDetails, setShowMeetingDetails] = useState(false)
    // to track the current selected meeting
    const [selectedMeetingId, setSelectedMeetingId] = useState(null)
    const monthNames = [
        "January", "February", "March", "April", "May", "June",
        "July", "August", "September", "October", "November", "December",
    ]

    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]


    const getDaysInMonth = (date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate()
    }

    const clampStartToMonthWindow = (dateObj) => {
        const daysInMonth = getDaysInMonth(dateObj)
        const maxStart = Math.max(1, daysInMonth - 6)
        const start = Math.min(dateObj.getDate(), maxStart)
        return new Date(dateObj.getFullYear(), dateObj.getMonth(), start)
    }

    const changeMonth = (direction) => {
        const target = new Date(
            currentDate.getFullYear(),
            currentDate.getMonth() + direction,
            1
        )
        setCurrentDate(target)

        const today = new Date()
        if (
            target.getFullYear() === today.getFullYear() &&
            target.getMonth() === today.getMonth()
        ) {
            setSelectedDay(today.getDate())
            setVisibleStartDate(clampStartToMonthWindow(today))
            setSelectedDateForApi(today)
        } else {
            const firstDayOfMonth = 1
            setSelectedDay(firstDayOfMonth)
            setVisibleStartDate(
                new Date(target.getFullYear(), target.getMonth(), firstDayOfMonth)
            )
            setSelectedDateForApi(
                new Date(target.getFullYear(), target.getMonth(), firstDayOfMonth)
            )
        }
    }

    const getVisibleDays = () => {
        const days = []
        const year = visibleStartDate.getFullYear()
        const month = visibleStartDate.getMonth()
        const startDate = visibleStartDate.getDate()

        for (let i = 0; i < 7; i++) {
            const date = new Date(year, month, startDate + i)
            days.push({
                day: dayNames[date.getDay()],
                date: date.getDate(),
                fullDate: date,
                isCurrentMonth: date.getMonth() === currentDate.getMonth(),
            })
        }
        return days
    }

    const handlePrevious = () => {
        const year = visibleStartDate.getFullYear()
        const month = visibleStartDate.getMonth()
        const newDate = new Date(year, month, visibleStartDate.getDate() - 7)

        if (
            newDate.getMonth() !== currentDate.getMonth() ||
            newDate.getFullYear() !== currentDate.getFullYear()
        ) {
            setVisibleStartDate(
                new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
            )
        } else {
            setVisibleStartDate(newDate)
        }
    }

    const handleNext = () => {
        const year = visibleStartDate.getFullYear()
        const month = visibleStartDate.getMonth()
        const daysInMonth = getDaysInMonth(currentDate)
        const newDate = new Date(year, month, visibleStartDate.getDate() + 7)

        if (
            newDate.getMonth() !== currentDate.getMonth() ||
            newDate.getFullYear() !== currentDate.getFullYear()
        ) {
            const lastVisibleStart = Math.max(1, daysInMonth - 6)
            setVisibleStartDate(
                new Date(currentDate.getFullYear(), currentDate.getMonth(), lastVisibleStart)
            )
        } else if (newDate.getDate() + 6 > daysInMonth) {
            const lastVisibleStart = Math.max(1, daysInMonth - 6)
            setVisibleStartDate(
                new Date(currentDate.getFullYear(), currentDate.getMonth(), lastVisibleStart)
            )
        } else {
            setVisibleStartDate(newDate)
        }
    }

    const isPreviousDisabled = () => {
        return (
            visibleStartDate.getDate() === 1 &&
            visibleStartDate.getMonth() === currentDate.getMonth() &&
            visibleStartDate.getFullYear() === currentDate.getFullYear()
        )
    }

    const isNextDisabled = () => {
        const daysInMonth = getDaysInMonth(currentDate)
        const lastVisibleDate = visibleStartDate.getDate() + 6
        return (
            lastVisibleDate >= daysInMonth &&
            visibleStartDate.getMonth() === currentDate.getMonth() &&
            visibleStartDate.getFullYear() === currentDate.getFullYear()
        )
    }

    const visibleDays = getVisibleDays()

    const formatDateForAPI = (date) => {
        const d = new Date(date)
        const day = String(d.getDate()).padStart(2, "0")
        const month = String(d.getMonth() + 1).padStart(2, "0")
        const year = d.getFullYear()
        return `${day}-${month}-${year}`
    }
    const fetchMeetings = async () => {
        setIsLoadingMeetings(true)
        try {
            const res = await GetRequest(
                `taskboard/meetings/${customer.customer_details?.customer_id}/${customer.customer_details?.id
                }/${customer.customer_details?.user_type}?date=${formatDateForAPI(selectedDateForApi)}`,
                { headers },
                extras
            )
            if (res.success === false) {
                setIsLoadingMeetings(false)
                return
            }

            if (res.status === "fail") {
                setMeetings(null)
                setIsLoadingMeetings(false)
                return
            }
            const data = res.data?.['todays_meeting']?.length > 0 ? Object.fromEntries(
                res.data['todays_meeting'].map(c => [c.id, c])
            )
                : null
            const historyData = res.data?.['history']?.length > 0 ? Object.fromEntries(
                res.data['history'].map(c => [c.id, c])
            ) : null

            setSelectedMeetingId(res.data?.todays_meeting?.[0]?.id)
            setCurrentMeetingData(res?.data?.todays_meeting?.[0] ?? null)
            setMeetingStatus(res?.data?.todays_meeting?.[0]?.meeting_status)
            setMeetings(data)
            setHistoryMeetings(historyData)
            setIsLoadingMeetings(false)
        } catch (error) {
            setIsLoadingMeetings(false)
            return
        }
    }

    useEffect(() => {
        fetchMeetings()
    }, [selectedDateForApi])

    return (
        <Row
            className="Projectscard w-100 mx-0 g-0"
            style={{ minHeight: "calc(100vh - 12rem)", }}
        >
            <Col md={5} style={{ borderRight: "1px solid rgb(224, 228, 248)" }}>
                {/* Card Header */}
                <div className="card-header d-flex align-items-center justify-content-between calendars mb-2 pb-0 w-100">
                    <h3>
                        <FontAwesomeIcon icon={faCalendar} style={{ color: "#5d5fef" }} />{" "}
                        Meetings
                    </h3>
                    <div className="calendar-month-nav" style={{ marginBottom: "0.8rem" }}>
                        <button onClick={() => changeMonth(-1)} className="calendar-nav-btn">
                            <ChevronLeft className="calendar-nav-icon" />
                        </button>
                        <div className="calendar-month-display">
                            <span className="calendar-month-text">
                                {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
                            </span>
                        </div>
                        <button onClick={() => changeMonth(1)} className="calendar-nav-btn">
                            <ChevronRight className="calendar-nav-icon" />
                        </button>
                    </div>
                </div>

                {/* Calendar */}
                <div className="calendar mt-0">
                    <div className="calendar-days mb-0">
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                                overflow: "hidden",
                            }}
                        >
                            <div
                                className="calendar-days-container"
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    width: "100%",
                                    overflowX: "auto",
                                    scrollbarWidth: "none",
                                    msOverflowStyle: "none",
                                }}
                            >
                                {/* Prev arrow — never shrinks */}
                                <button
                                    onClick={handlePrevious}
                                    disabled={isPreviousDisabled()}
                                    className={`calendar-day-nav-btn ${isPreviousDisabled() ? "calendar-nav-disabled" : ""
                                        }`}
                                    style={{ flexShrink: 0 }}
                                >
                                    <ChevronLeft className="calendar-nav-icon" />
                                </button>

                                {/* Day buttons — each gets a fixed 44 px slot */}
                                {visibleDays.map((item, index) => (
                                    <button
                                        key={index}
                                        onClick={() => {
                                            setSelectedDay(item.date)
                                            setSelectedDateForApi(item.fullDate)
                                        }}
                                        style={{
                                            lineHeight: 0.8,
                                            width: "44px",
                                            minWidth: "44px",
                                            flexShrink: 0,
                                        }}
                                        className={`calendar-day-btn ${selectedDay === item.date && item.isCurrentMonth
                                            ? "calendar-day-selected"
                                            : item.isCurrentMonth
                                                ? "calendar-day-active"
                                                : "calendar-day-inactive"
                                            }`}
                                        disabled={!item.isCurrentMonth}
                                    >
                                        <span className="calendar-day-name">{item.day}</span>
                                        <span className="calendar-day-number">{item.date}</span>
                                    </button>
                                ))}

                                {/* Next arrow — never shrinks */}
                                <button
                                    onClick={handleNext}
                                    disabled={isNextDisabled()}
                                    className={`calendar-day-nav-btn ${isNextDisabled() ? "calendar-nav-disabled" : ""
                                        }`}
                                    style={{ flexShrink: 0 }}
                                >
                                    <ChevronRight className="calendar-nav-icon" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Meetings List */}
                    <div
                        style={{
                            flex: 1,
                            minHeight: "calc(100vh - 400px)",
                            maxHeight: "calc(100vh - 510px)",
                            overflowY: "scroll",
                            scrollBehavior: "smooth",
                            width: "100%",
                            scrollbarWidth: "thin",
                            scrollbarColor: "transparent transparent",
                            transition: "scrollbar-color 0.3s ease",
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.scrollbarColor = "#888 #f1f1f1"
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.scrollbarColor = "transparent transparent"
                        }}
                        className="position-relative d-flex flex-column gap-3 px-3 meetings-scroll-container"
                    >
                        {isLoadingMeetings ? (
                            <div
                                className="d-flex justify-content-center align-items-center"
                                style={{ height: "100%", minHeight: "200px" }}
                            >
                                <div
                                    className="spinner-border"
                                    role="status"
                                    style={{
                                        color: "#5d5fef",
                                        width: "2.5rem",
                                        height: "2.5rem",
                                    }}
                                >
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : meetings !== null ? (
                            Object.values(meetings)?.map((meeting, index) => {
                                const cardColors = ["#f5f4fe", "#fff5f4"]
                                const cardColor = cardColors[index % cardColors.length]
                                const invitees = meeting?.invitees
                                    ? JSON.parse(meeting?.invitees)
                                    : []
                                const showMoreIndicator = invitees.length > 5
                                const moreCount = invitees.length - 5

                                const getStableAvatar = (invitee, idx) => {
                                    const seed = invitee.email || invitee.id || idx
                                    return `https://i.pravatar.cc/40?img=${(Math.abs(
                                        seed
                                            .toString()
                                            .split("")
                                            .reduce((a, b) => a + b.charCodeAt(0), 0)
                                    ) % 10) + 1}`
                                }

                                const handleMeetingCard = () => {
                                    setSelectedMeetingId(meeting.id)
                                    setCurrentMeetingData(meetings[meeting.id])
                                    setShowMeetingDetails(true)
                                }

                                return (
                                    <MeetingCard
                                        meeting={meeting}
                                        showMoreIndicator={showMoreIndicator}
                                        moreCount={moreCount}
                                        cardColor={cardColor}
                                        onClickMeetingCard={handleMeetingCard}
                                        invitees={invitees}
                                        meetingCategory={'current'}
                                    />
                                )
                            })
                        ) : (
                            <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted p-5 border rounded-3 bg-light-subtle" style={{ borderStyle: 'dashed !important' }}>
                                <IconChecklist size={48} stroke={1} className="mb-3 opacity-50" />
                                <p className="fw-medium">No meetings for today</p>
                            </div>
                        )}

                        <>
                            <h6>Meeting History</h6>
                            {
                                historyMeetings !== null ? (
                                    Object.values(historyMeetings)?.map((meeting, index) => {
                                        const cardColors = ["#f5f4fe", "#fff5f4"]
                                        const cardColor = cardColors[index % cardColors.length]
                                        const invitees = meeting?.invitees
                                            ? JSON.parse(meeting?.invitees)
                                            : []
                                        const showMoreIndicator = invitees.length > 5
                                        const moreCount = invitees.length - 5

                                        const getStableAvatar = (invitee, idx) => {
                                            const seed = invitee.email || invitee.id || idx
                                            return `https://i.pravatar.cc/40?img=${(Math.abs(
                                                seed
                                                    .toString()
                                                    .split("")
                                                    .reduce((a, b) => a + b.charCodeAt(0), 0)
                                            ) % 10) + 1}`
                                        }
                                        const handleMeetingCard = () => {
                                            setSelectedMeetingId(meeting.id)
                                            setCurrentMeetingData(historyMeetings[meeting.id])
                                            setShowMeetingDetails(true)
                                        }

                                        return (
                                            <MeetingCard
                                                meeting={meeting}
                                                showMoreIndicator={showMoreIndicator}
                                                moreCount={moreCount}
                                                cardColor={cardColor}
                                                onClickMeetingCard={handleMeetingCard}
                                                invitees={invitees}
                                                meetingCategory={'history'}
                                            />
                                        )
                                    })
                                )
                                    :
                                    <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted p-5 border rounded-3 bg-light-subtle" style={{ borderStyle: 'dashed !important' }}>
                                        <IconChecklist size={48} stroke={1} className="mb-3 opacity-50" />
                                        <p className="fw-medium">No meetings history</p>
                                    </div>
                            }

                        </>
                    </div>

                    <style jsx>{`
                        /* Hide the horizontal scrollbar on the days strip */
                        .calendar-days-container::-webkit-scrollbar {
                            display: none;
                        }

                        .meetings-scroll-container::-webkit-scrollbar {
                            width: 8px;
                        }
                        .meetings-scroll-container::-webkit-scrollbar-track {
                            background: #f1f1f1;
                            border-radius: 10px;
                        }
                        .meetings-scroll-container::-webkit-scrollbar-thumb {
                            background: #888;
                            border-radius: 10px;
                        }
                        .meetings-scroll-container::-webkit-scrollbar-thumb:hover {
                            background: #555;
                        }
                    `}</style>
                </div>
            </Col>
            <Col md={7} style={{ paddingLeft: "5px" }}>
                {
                    (currentMeetingData && typeof currentMeetingData === "object" && !Array.isArray(currentMeetingData)) ?
                        <MeetingDetails
                            customer={customer}
                            currMeetingStatus={currMeetingStatus}
                            setMeetingStatus={setMeetingStatus}
                            meetingData={currentMeetingData}
                            headers={headers}
                            extras={extras}
                            fetchMeetings={fetchMeetings}
                            selectedMeetingId={selectedMeetingId}
                        />
                        :
                        <div className="h-100 d-flex flex-column align-items-center justify-content-center text-muted p-5 border rounded-3 bg-light-subtle" style={{ borderStyle: 'dashed !important' }}>
                            <IconChecklist size={48} stroke={1} className="mb-3 opacity-50" />
                            <p className="fw-medium">Select a Meeting from the list to view details</p>
                        </div>
                }
            </Col>
        </Row >
    )
}